// The panels of a sheet's own objects (a dimension, a text box, a line, an image,
// a connection: SHEET_PANELS, one per kind) and of several of them selected.
// docs/claude/sheets.md.
import {
  CONNECTION_TYPES,
  type SchematicBox,
  type SchematicConn,
  type SchematicDim,
  type SchematicLine,
  type SchematicLineStyle,
  type Sheet,
} from '../../shared/types';
import {
  invalidate,
  pushHistory,
  renderProperties,
} from './core';
import { tr } from '../i18n';
import {
  buttonRow,
  colorRow,
  deleteRow,
  infoRow,
  numberRow,
  opacityRow,
  sectionTitle,
  selectRow,
  sliderRow,
} from './panelRows';
import {
  paperDimFigure,
  paperDimOffset,
} from '../paperDim';
import {
  CONNECTION_COLORS,
  TEXTBOX_CORNER_DEFAULT,
} from '../render';
import {
  boxFontMm,
  MM_PER_PT,
  SCH_CONN_THICKNESS,
  type SchemSel,
} from '../schematic';
import { schContent } from '../sheetObjects';
import { renderViewportPanel } from './sheetPanel';
import { formatLen } from '../main';
import {
  schemSel,
  schemSelSet,
} from './sheetSpace';
import {
  appendSchStyleButtons,
  deleteSchematicSelected,
  lineStyleRow,
  rememberConnStyle,
} from './sheetSchematic';
import { renderElecViewPanel } from './elecSheet';

/** Properties of the selected schematic object (or a hint when none is selected). */
/** Properties panel for a MULTI-selection of schematic objects: only the axes
 *  common to the selected kinds — colour (box border / line / conn), and, when
 *  every member is a line or connection, thickness and dash style. Each edit
 *  applies to the whole selection in one undo step. */
export function renderSchematicMultiProps(body: HTMLElement, sh: Sheet): void {
  // Read, never created: two views selected leave the sheet as it was.
  const c = sh.schematic ?? { boxes: [], lines: [], conns: [], images: [] };
  const sels = schemSelSet;
  body.appendChild(sectionTitle(`${sels.length} ${tr('objects selected')}`));
  const box = (id: string): SchematicBox | undefined => c.boxes.find((x) => x.id === id);
  const line = (id: string): SchematicLine | undefined => c.lines.find((x) => x.id === id);
  const conn = (id: string): SchematicConn | undefined => c.conns.find((x) => x.id === id);
  const hasColour = sels.some((s) => s.kind === 'box' || s.kind === 'line' || s.kind === 'conn');
  const allStroke = sels.every((s) => s.kind === 'line' || s.kind === 'conn');
  // Representative current values come from the primary (last-picked) object.
  const p = schemSel!;
  const repColour = p.kind === 'box' ? (box(p.id)?.borderColor ?? '#1b1d22')
    : p.kind === 'line' ? (line(p.id)?.color ?? '#1b1d22')
      : p.kind === 'conn' ? (conn(p.id)?.color ?? CONNECTION_COLORS[conn(p.id)?.type ?? 'electrical']) : '#1b1d22';
  const repThickness = p.kind === 'line' ? (line(p.id)?.thickness ?? 0.6) : (conn(p.id)?.thickness ?? SCH_CONN_THICKNESS);
  const repStyle: SchematicLineStyle = p.kind === 'line' ? (line(p.id)?.style ?? 'solid') : (conn(p.id)?.style ?? 'dashed');

  // All text boxes → their full shared set: size and all three colours + opacities,
  // each edit applied to every selected box.
  if (sels.every((s) => s.kind === 'box')) {
    const pb = box(p.id);
    const eachBox = (fn: (b: SchematicBox) => void): void => { for (const s of sels) { const b = box(s.id); if (b) fn(b); } };
    body.appendChild(numberRow('Width (mm)', pb?.w ?? 0, (v) => { if (v > 0) { pushHistory(); eachBox((b) => (b.w = v)); invalidate(); } }, { commit: true }));
    body.appendChild(numberRow('Height (mm)', pb?.h ?? 0, (v) => { if (v > 0) { pushHistory(); eachBox((b) => (b.h = v)); invalidate(); } }, { commit: true }));
    body.appendChild(colorRow('Text colour', pb?.textColor ?? '#1b1d22', (hex) => { eachBox((b) => (b.textColor = hex)); invalidate(); }));
    body.appendChild(opacityRow('Text opacity', pb?.textOpacity ?? 1, (v) => eachBox((b) => (b.textOpacity = v))));
    body.appendChild(colorRow('Border colour', pb?.borderColor ?? '#1b1d22', (hex) => { eachBox((b) => (b.borderColor = hex)); invalidate(); }));
    body.appendChild(opacityRow('Border opacity', pb?.borderOpacity ?? 1, (v) => eachBox((b) => (b.borderOpacity = v))));
    body.appendChild(colorRow('Fill colour', pb?.fillColor ?? '#ffffff', (hex) => { eachBox((b) => (b.fillColor = hex)); invalidate(); }));
    body.appendChild(opacityRow('Fill opacity', pb?.fillOpacity ?? 1, (v) => eachBox((b) => (b.fillOpacity = v))));
    body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
    return;
  }

  if (hasColour) body.appendChild(colorRow('Colour', repColour, (hex) => {
    for (const s of sels) {
      if (s.kind === 'box') { const b = box(s.id); if (b) b.borderColor = hex; }
      else if (s.kind === 'line') { const l = line(s.id); if (l) l.color = hex; }
      else if (s.kind === 'conn') { const cn = conn(s.id); if (cn) cn.color = hex; }
    }
    invalidate();
  }));
  if (allStroke) {
    body.appendChild(numberRow('Line width (mm)', repThickness, (v) => {
      pushHistory();
      for (const s of sels) { if (s.kind === 'line') { const l = line(s.id); if (l) l.thickness = Math.max(0.1, v); } else { const cn = conn(s.id); if (cn) cn.thickness = Math.max(0.1, v); } }
      invalidate();
    }, { commit: true }));
    body.appendChild(lineStyleRow(repStyle, (v) => {
      pushHistory();
      for (const s of sels) { if (s.kind === 'line') { const l = line(s.id); if (l) l.style = v; } else { const cn = conn(s.id); if (cn) cn.style = v; } }
      invalidate();
    }));
  }
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

export function renderSchematicObjectProps(body: HTMLElement, sh: Sheet): void {
  // An electrical view stands on a sheet with no schematic content.
  if (!schemSel || (!sh.schematic && schemSel.kind !== 'elec')) {
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Add boxes, lines, connections and images from the schematic toolbar. Click an object to edit it here.');
    body.appendChild(hint);
    return;
  }
  const sel = schemSel;
  SHEET_PANELS[sel.kind](body, sh, sel.id);
}

/** Each sheet object kind's panel: a new kind does not compile without one. */
const SHEET_PANELS: Record<SchemSel['kind'], (body: HTMLElement, sh: Sheet, id: string) => void> = {
  box: renderSheetBoxPanel,
  conn: renderSheetConnPanel,
  dim: renderSheetDimPanel,
  elec: renderElecViewPanel,
  image: renderSheetImagePanel,
  line: renderSheetLinePanel,
  viewport: renderViewportPanel,
};

/** A selected sheet dimension's panel. */
function renderSheetDimPanel(body: HTMLElement, sh: Sheet, id: string): void {
  const c = schContent(sh);
  const sel = { kind: 'dim' as const, id };
  const dm = c.dims?.find((x) => x.id === sel.id); if (!dm) return;
  const f = paperDimFigure(dm, sh.viewports);
  body.appendChild(sectionTitle('Dimension'));
  if (f) {
    body.appendChild(infoRow('Length', formatLen(f.mm)));
    body.appendChild(infoRow('Measures in', tr(f.model ? 'the model, through its viewport' : 'paper')));
  }
  body.appendChild(selectRow('Measures', [['aligned', 'Aligned'], ['horizontal', 'Horizontal'], ['vertical', 'Vertical']], dm.kind ?? 'aligned', (v) => {
    pushHistory();
    const at = f ? { x: (f.geom.a.x + f.geom.b.x) / 2, y: (f.geom.a.y + f.geom.b.y) / 2 } : dm.a;
    dm.kind = v === 'aligned' ? undefined : (v as SchematicDim['kind']);
    dm.offset = paperDimOffset(dm.a, dm.b, at, dm.kind); // it stays where it was put
    renderProperties(); invalidate();
  }));
  body.appendChild(numberRow('Offset (mm)', Math.round(dm.offset * 10) / 10, (v) => { pushHistory(); dm.offset = v; invalidate(); }, { commit: true }));
  body.appendChild(colorRow('Colour', dm.color ?? '#1b1d22', (hex) => { dm.color = hex; invalidate(); }));
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

/** A selected sheet box's panel. */
function renderSheetBoxPanel(body: HTMLElement, sh: Sheet, id: string): void {
  const c = schContent(sh);
  const sel = { kind: 'box' as const, id };
  const b = c.boxes.find((x) => x.id === sel.id); if (!b) return;
  body.appendChild(sectionTitle('Text box'));
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr('Text');
  const ta = document.createElement('textarea'); ta.rows = 2; ta.value = b.text; ta.style.resize = 'vertical';
  ta.addEventListener('input', () => { b.text = ta.value; invalidate(); });
  ta.addEventListener('change', () => pushHistory());
  row.append(lab, ta); body.appendChild(row);
  body.appendChild(numberRow('Width (mm)', b.w, (v) => { if (v > 0) { pushHistory(); b.w = v; renderProperties(); invalidate(); } }, { commit: true }));
  body.appendChild(numberRow('Height (mm)', b.h, (v) => { if (v > 0) { pushHistory(); b.h = v; renderProperties(); invalidate(); } }, { commit: true }));
  body.appendChild(sliderRow('Corner radius (mm)', b.cornerRadius ?? TEXTBOX_CORNER_DEFAULT, 0, Math.max(1, Math.round(Math.min(b.w, b.h) / 2)), 1, (v) => { b.cornerRadius = v; }));
  body.appendChild(colorRow('Text colour', b.textColor ?? '#1b1d22', (hex) => { b.textColor = hex; invalidate(); }));
  body.appendChild(opacityRow('Text opacity', b.textOpacity ?? 1, (v) => { b.textOpacity = v; }));
  body.appendChild(colorRow('Border colour', b.borderColor ?? '#1b1d22', (hex) => { b.borderColor = hex; invalidate(); }));
  body.appendChild(opacityRow('Border opacity', b.borderOpacity ?? 1, (v) => { b.borderOpacity = v; }));
  body.appendChild(colorRow('Fill colour', b.fillColor ?? '#ffffff', (hex) => { b.fillColor = hex; invalidate(); }));
  body.appendChild(opacityRow('Fill opacity', b.fillOpacity ?? 1, (v) => { b.fillOpacity = v; }));
  if (b.fillColor) body.appendChild(buttonRow('Remove fill', () => { pushHistory(); b.fillColor = undefined; renderProperties(); invalidate(); }));
  // Type size in mm, like the model's text: stored in pt (the box's own unit).
  body.appendChild(numberRow('Text height (mm)', Math.round(boxFontMm(b) * 10) / 10, (v) => { pushHistory(); b.fontPt = Math.max(0.3, v) / MM_PER_PT; b.fontScale = undefined; invalidate(); }, { commit: true }));
  appendSchStyleButtons(body, sh, sel);
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

/** A selected sheet line's panel. */
function renderSheetLinePanel(body: HTMLElement, sh: Sheet, id: string): void {
  const c = schContent(sh);
  const sel = { kind: 'line' as const, id };
  const l = c.lines.find((x) => x.id === sel.id); if (!l) return;
  body.appendChild(sectionTitle(l.closed ? 'Polygon' : 'Line'));
  body.appendChild(colorRow('Colour', l.color, (hex) => { l.color = hex; invalidate(); }));
  if (l.closed) {
    body.appendChild(colorRow('Fill colour', l.fillColor ?? '#ffffff', (hex) => { l.fillColor = hex; renderProperties(); invalidate(); }));
    if (l.fillColor) body.appendChild(buttonRow('Remove fill', () => { pushHistory(); l.fillColor = undefined; renderProperties(); invalidate(); }));
  }
  body.appendChild(numberRow('Line width (mm)', l.thickness, (v) => { pushHistory(); l.thickness = Math.max(0.1, v); invalidate(); }, { commit: true }));
  body.appendChild(lineStyleRow(l.style, (v) => { pushHistory(); l.style = v; invalidate(); }));
  appendSchStyleButtons(body, sh, sel);
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

/** A selected sheet image's panel. */
function renderSheetImagePanel(body: HTMLElement, sh: Sheet, id: string): void {
  const c = schContent(sh);
  const sel = { kind: 'image' as const, id };
  const im = c.images.find((x) => x.id === sel.id); if (!im) return;
  body.appendChild(sectionTitle('Image'));
  body.appendChild(numberRow('Width (mm)', im.w, (v) => { pushHistory(); im.w = Math.max(4, v); invalidate(); }, { commit: true }));
  body.appendChild(numberRow('Height (mm)', im.h, (v) => { pushHistory(); im.h = Math.max(4, v); invalidate(); }, { commit: true }));
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

/** A selected sheet connection's panel. */
function renderSheetConnPanel(body: HTMLElement, sh: Sheet, id: string): void {
  const c = schContent(sh);
  const sel = { kind: 'conn' as const, id };
  const cn = c.conns.find((x) => x.id === sel.id); if (!cn) return;
  body.appendChild(sectionTitle('Connection'));
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr('Discipline');
  const dSel = document.createElement('select');
  for (const t of CONNECTION_TYPES) { const o = document.createElement('option'); o.value = t; o.textContent = tr(t[0].toUpperCase() + t.slice(1)); if ((cn.type ?? 'electrical') === t) o.selected = true; dSel.appendChild(o); }
  dSel.addEventListener('change', () => { pushHistory(); cn.type = dSel.value as SchematicConn['type']; rememberConnStyle(cn); invalidate(); });
  row.append(lab, dSel); body.appendChild(row);
  // Colour: overrides the discipline colour when set.
  body.appendChild(colorRow('Colour', cn.color ?? CONNECTION_COLORS[cn.type ?? 'electrical'], (hex) => { cn.color = hex; rememberConnStyle(cn); invalidate(); }));
  if (cn.color) body.appendChild(buttonRow('Use discipline colour', () => { pushHistory(); cn.color = undefined; rememberConnStyle(cn); renderProperties(); invalidate(); }));
  body.appendChild(numberRow('Line width (mm)', cn.thickness ?? SCH_CONN_THICKNESS, (v) => { pushHistory(); cn.thickness = Math.max(0.1, v); rememberConnStyle(cn); invalidate(); }, { commit: true }));
  body.appendChild(lineStyleRow(cn.style ?? 'dashed', (v) => { pushHistory(); cn.style = v; rememberConnStyle(cn); invalidate(); }));
  body.appendChild(numberRow('Parallel lines', cn.lines, (v) => { pushHistory(); cn.lines = Math.max(1, Math.round(v)); rememberConnStyle(cn); invalidate(); }, { commit: true }));
  const lrow = document.createElement('div'); lrow.className = 'prop-row';
  const llab = document.createElement('label'); llab.textContent = tr('Label');
  const linp = document.createElement('input'); linp.type = 'text'; linp.value = cn.label ?? '';
  linp.addEventListener('input', () => { cn.label = linp.value; invalidate(); });
  linp.addEventListener('change', () => pushHistory());
  lrow.append(llab, linp); body.appendChild(lrow);
  const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.marginTop = '8px';
  hint.textContent = tr('Double-click the line to add a routing point; drag points with the Select tool; right-click a point to remove it.');
  body.appendChild(hint);
  appendSchStyleButtons(body, sh, sel);
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}
